import React, { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';

const STORAGE_KEY = 'jrf_pwa_prompt_dismissed_v1';
const REMIND_DAYS = 7; // Volver a proponer instalación tras 7 días si la desestimaron

const isDismissedRecently = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < REMIND_DAYS * 24 * 60 * 60 * 1000;
  } catch (e) {
    return false;
  }
};

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const PWAInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Si ya está instalada como PWA o el usuario descartó recientemente, no mostrar
    if (isStandalone() || isDismissedRecently()) return;

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Retraso pequeño para dar tiempo a la web a cargar
      setTimeout(() => setVisible(true), 3500);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // iOS no dispara beforeinstallprompt: mostrar guía manual tras un delay
    if (isIOS()) {
      setTimeout(() => setVisible(true), 5000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (e) { /* noop */ }
    setVisible(false);
    setShowIOSGuide(false);
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        dismiss();
      }
      setDeferredPrompt(null);
    } else if (isIOS()) {
      setShowIOSGuide(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Banner inferior */}
      <div
        className="fixed bottom-4 inset-x-4 z-[9998] bg-black text-white shadow-2xl border border-neutral-800 max-w-md mx-auto animate-slideUp"
        data-testid="pwa-install-banner"
      >
        <div className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-white text-black rounded flex items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-black tracking-tighter">JRF</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-0.5">
              Instalar App
            </p>
            <p className="text-sm font-semibold leading-tight">
              JRF Sportswear en tu móvil
            </p>
          </div>
          <button
            onClick={install}
            className="bg-white text-black text-[11px] font-bold uppercase tracking-[0.15em] px-4 py-2.5 hover:bg-neutral-200 transition-colors flex items-center gap-1.5 flex-shrink-0"
            data-testid="pwa-install-btn"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={2} />
            Instalar
          </button>
          <button
            onClick={dismiss}
            className="text-white/60 hover:text-white transition-colors flex-shrink-0"
            aria-label="Cerrar"
            data-testid="pwa-dismiss-btn"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Guía iOS (Safari no soporta beforeinstallprompt) */}
      {showIOSGuide && (
        <div
          className="fixed inset-0 z-[9999] bg-black/70 flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0"
          onClick={dismiss}
        >
          <div
            className="bg-white text-black w-full max-w-md p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={dismiss}
              className="absolute top-4 right-4 hover:opacity-60"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-3">
              Cómo instalar en iPhone
            </p>
            <h3 className="text-2xl font-black uppercase tracking-tight mb-6 leading-none">
              JRF en tu iPhone
              <br />
              en 3 pasos
            </h3>
            <ol className="space-y-4 text-sm text-neutral-700">
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-black text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                <span>
                  Pulsa el icono{' '}
                  <Share className="inline w-4 h-4 mx-0.5 align-middle" strokeWidth={1.5} /> compartir de Safari (abajo del navegador)
                </span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-black text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                <span>Baja y pulsa <strong>&quot;Añadir a pantalla de inicio&quot;</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-black text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                <span>Pulsa <strong>&quot;Añadir&quot;</strong> arriba a la derecha. ¡Listo!</span>
              </li>
            </ol>
            <button
              onClick={dismiss}
              className="mt-8 w-full bg-black text-white py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-neutral-800 transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(120%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slideUp { animation: slideUp 0.4s ease-out; }
      `}</style>
    </>
  );
};

export default PWAInstallBanner;
